// Shared, accessible, viewport-aware modal shell (My Polaris Phase 2B).
//
// A thin wrapper over the project's already-installed Headless UI v1.7 `Dialog`.
// Headless UI owns dialog semantics, the portal, focus containment/restoration,
// background inerting, and document scroll locking. The shell adds: usable-height
// positioning (CSS-variable driven, no JavaScript viewport math), a single
// intentional body scroller with reachable header/footer, static size/tone class
// maps, and independent backdrop / Escape / disabled-close controls that Headless
// UI's undifferentiated dismiss callback cannot express on its own.
//
// @docs:
// - docs/planning/MY_POLARIS_KEYBOARD_GEOMETRY.md

import { useCallback, useRef } from 'react';
import type {
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  MutableRefObject,
  PointerEvent as ReactPointerEvent,
  ReactNode,
  Ref,
} from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import { useViewport } from '../../providers/ViewportProvider';

export type ModalShellSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl';
export type ModalShellTone = 'dark' | 'light';

export interface ModalShellProps {
  /** Controlled visibility. When false the shell renders nothing. */
  open: boolean;
  /** Close request from an approved dismiss path (button / backdrop / Escape). */
  onClose: () => void;
  /** Visible dialog title; also the accessible name. Required. */
  title: ReactNode;
  /** Sole intentional body scroller content. */
  children: ReactNode;
  description?: ReactNode;
  footer?: ReactNode;
  size?: ModalShellSize;
  tone?: ModalShellTone;
  /**
   * Vertical placement of the panel within the usable-height region.
   * - 'center' (default): always centred — current behaviour.
   * - 'above': centred while the keyboard is hidden, and bottom-aligned (just
   *   above HeliBoard, via the region's existing `py-4`) while it is visible.
   * Opt-in; consumers pass no geometry — the shell reads `useViewport()`.
   */
  keyboardAlign?: 'center' | 'above';
  closeOnBackdrop?: boolean;
  closeOnEscape?: boolean;
  showCloseButton?: boolean;
  closeDisabled?: boolean;
  /** Overrides default initial focus. Passed straight to Headless UI. */
  initialFocusRef?: MutableRefObject<HTMLElement | null>;
  /** Exposes the shell-owned body scroller for a later focus-reveal hook. */
  scrollBodyRef?: Ref<HTMLDivElement>;
  panelClassName?: string;
  bodyClassName?: string;
  footerClassName?: string;
}

/** Static width map — never build Tailwind class names dynamically. */
const SIZE_MAX_WIDTH: Record<ModalShellSize, string> = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-3xl',
  '2xl': 'max-w-4xl',
};

interface ToneClasses {
  panel: string;
  headerBorder: string;
  title: string;
  description: string;
  closeButton: string;
  footerBorder: string;
}

/** Static tone maps — the palette mechanism, not consumer class overrides. */
const TONE_CLASSES: Record<ModalShellTone, ToneClasses> = {
  dark: {
    panel: 'bg-slate-800 text-slate-100 border border-slate-700',
    headerBorder: 'border-slate-700',
    title: 'text-white',
    description: 'text-slate-400',
    closeButton: 'text-slate-400 hover:text-white hover:bg-slate-700',
    footerBorder: 'border-slate-700',
  },
  light: {
    panel: 'bg-white text-gray-900 border border-gray-200',
    headerBorder: 'border-gray-200',
    title: 'text-gray-900',
    description: 'text-gray-500',
    closeButton: 'text-gray-400 hover:text-gray-600 hover:bg-gray-100',
    footerBorder: 'border-gray-200',
  },
};

// The Phase 2A provider publishes `--vortex-usable-height` (already net of any
// keyboard occlusion). The modal derives its region height and panel cap from the
// SAME `useViewport()` read that selects the alignment, so both commit together in
// one render; these CSS-variable forms are the standalone fallback used when no
// valid context height is available (e.g. outside a provider). The panel is capped
// to the usable height minus the 1rem top + 1rem bottom safe gap. The separate
// keyboard inset variable is deliberately never referenced here, to avoid
// double-counting occlusion that the usable-height value already accounts for.
const USABLE_HEIGHT = 'var(--vortex-usable-height, 100vh)';
const PANEL_MAX_HEIGHT = 'calc(var(--vortex-usable-height, 100vh) - 2rem)';

export function ModalShell({
  open,
  onClose,
  title,
  children,
  description,
  footer,
  size = 'md',
  tone = 'dark',
  keyboardAlign = 'center',
  closeOnBackdrop = true,
  closeOnEscape = true,
  showCloseButton = true,
  closeDisabled = false,
  initialFocusRef,
  scrollBodyRef,
  panelClassName,
  bodyClassName,
  footerClassName,
}: ModalShellProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  // Opt-in: bottom-align the panel within the usable-height region while the
  // keyboard is visible, so a short panel sits just above HeliBoard instead of
  // floating centred. Reads the shared normalized state (safe fallback:
  // keyboardVisible=false, no context height, outside a provider). Only the
  // region's align class and its height/panel cap change — never the panel/DOM
  // tree — so focus and content are preserved.
  const { keyboardVisible, usableHeightCssPx } = useViewport();
  const regionAlignClass =
    keyboardAlign === 'above' && keyboardVisible ? 'items-end' : 'items-center';

  // Atomic positioning: derive the region height and panel max-height from the SAME
  // viewport-context read that selected the alignment above, so height and alignment
  // always change together in one React commit. This closes the split where the
  // provider's synchronously-published `--vortex-usable-height` shrank the region one
  // paint before React applied the new alignment — which briefly painted a
  // reduced-height centred panel on first focus. When the context has no valid
  // positive height (e.g. DEFAULT_STATE outside a provider), fall back to the
  // CSS-variable forms so standalone rendering stays safe.
  const hasContextHeight =
    Number.isFinite(usableHeightCssPx) && usableHeightCssPx > 0;
  const regionHeight = hasContextHeight ? `${usableHeightCssPx}px` : USABLE_HEIGHT;
  const panelMaxHeight = hasContextHeight
    ? `calc(${usableHeightCssPx}px - 2rem)`
    : PANEL_MAX_HEIGHT;

  // True only while a pointer press began on the backdrop itself.
  const pointerDownOnBackdrop = useRef(false);

  // The single close-request path. Every approved dismiss routes through here and
  // it is the only place that may call the consumer's onClose.
  const requestClose = useCallback(() => {
    if (closeDisabled) return;
    onClose();
  }, [closeDisabled, onClose]);

  // Headless UI cannot distinguish Escape from outside-click and gives no reason,
  // so its dismiss callback is intentionally inert; the shell owns both paths.
  const handleHeadlessDismiss = useCallback(() => {
    /* intentionally inert — see comment above */
  }, []);

  const handlePanelKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      if (event.key !== 'Escape') return;
      // Shell owns Escape: stop it reaching Headless UI's handler or any dialog
      // beneath this one, whether or not it results in a close.
      event.stopPropagation();
      event.preventDefault();
      if (closeOnEscape) requestClose();
    },
    [closeOnEscape, requestClose],
  );

  const handleBackdropPointerDown = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    pointerDownOnBackdrop.current = event.target === event.currentTarget;
  }, []);

  const handleBackdropPointerCancel = useCallback(() => {
    pointerDownOnBackdrop.current = false;
  }, []);

  const handleBackdropClick = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      const beganAndEndedOnBackdrop =
        pointerDownOnBackdrop.current && event.target === event.currentTarget;
      pointerDownOnBackdrop.current = false;
      if (!beganAndEndedOnBackdrop) return;
      if (closeOnBackdrop) requestClose();
    },
    [closeOnBackdrop, requestClose],
  );

  const handleCloseButtonClick = useCallback(() => {
    requestClose();
  }, [requestClose]);

  // Default focus never lands on content (which could open the Scout keyboard):
  // the enabled close button, else the focusable panel; a consumer ref wins.
  const resolvedInitialFocus = (initialFocusRef ??
    (showCloseButton && !closeDisabled
      ? closeButtonRef
      : panelRef)) as MutableRefObject<HTMLElement | null>;

  const toneClasses = TONE_CLASSES[tone];

  return (
    <Dialog
      open={open}
      onClose={handleHeadlessDismiss}
      initialFocus={resolvedInitialFocus}
      className="relative z-50"
    >
      {/* Full-viewport backdrop — separate from the usable-height region. */}
      <div
        aria-hidden="true"
        className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm"
        onPointerDown={handleBackdropPointerDown}
        onPointerCancel={handleBackdropPointerCancel}
        onClick={handleBackdropClick}
      />

      {/* Positioning region: fixed to the top of the viewport, its height bound to
          the usable viewport so the panel centres above the keyboard on Scout.
          pointer-events-none lets clicks in the padding fall through to the
          backdrop; the panel re-enables pointer events. */}
      <div
        className={clsx(
          'pointer-events-none fixed left-0 right-0 top-0 z-50 box-border flex justify-center px-4 py-4',
          regionAlignClass,
        )}
        style={{ height: regionHeight }}
      >
        <Dialog.Panel
          ref={panelRef}
          tabIndex={-1}
          onKeyDown={handlePanelKeyDown}
          style={{ maxHeight: panelMaxHeight }}
          className={clsx(
            'pointer-events-auto flex w-full min-h-0 flex-col overflow-hidden rounded-2xl shadow-2xl outline-none',
            SIZE_MAX_WIDTH[size],
            toneClasses.panel,
            panelClassName,
          )}
        >
          <div
            className={clsx(
              'flex shrink-0 items-start justify-between gap-4 border-b px-5 py-4',
              toneClasses.headerBorder,
            )}
          >
            <div className="min-w-0">
              <Dialog.Title className={clsx('text-lg font-bold', toneClasses.title)}>
                {title}
              </Dialog.Title>
              {description != null && (
                <Dialog.Description className={clsx('mt-0.5 text-sm', toneClasses.description)}>
                  {description}
                </Dialog.Description>
              )}
            </div>
            {showCloseButton && (
              <button
                ref={closeButtonRef}
                type="button"
                aria-label="Close"
                disabled={closeDisabled}
                onClick={handleCloseButtonClick}
                className={clsx(
                  'shrink-0 rounded-lg p-2 transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                  toneClasses.closeButton,
                )}
              >
                <XMarkIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            )}
          </div>

          {/* The sole intentional vertical scroller. */}
          <div
            ref={scrollBodyRef}
            className={clsx(
              'min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-4',
              bodyClassName,
            )}
          >
            {children}
          </div>

          {footer != null && (
            <div
              className={clsx(
                'shrink-0 border-t px-5 py-4',
                toneClasses.footerBorder,
                footerClassName,
              )}
            >
              {footer}
            </div>
          )}
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}

export default ModalShell;
